import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import './company-picker.css';
import './partner-picker.css';

/** A partner as the picker lists it: its code and how many companies are linked to it now. */
export interface PickerPartner {
  code: string;
  companies: number;
}

/** A row that is always offered, whatever is typed: "No partner". */
export interface FixedPartner {
  value: string;
  label: string;
}

export interface PartnerPickerProps {
  /** The partner chosen: a code or a fixed row's value; null for every partner. */
  value: string | null;
  onChange: (partner: string | null) => void;
  /** Asks the server (`GET /v1/admin/partners?q=`): codes that start with what is typed, twenty at most. */
  search: (q: string) => Promise<PickerPartner[]>;
  allLabel?: string;
  /** The rows under "every partner" that are not codes. */
  fixed?: readonly FixedPartner[];
  debounceMs?: number;
}

const NO_PARTNER_ROW: readonly FixedPartner[] = [{ value: 'none', label: 'No partner' }];

interface Row {
  value: string | null;
  label: string;
  note?: string;
}

/** The server's answer for what is typed, asked once typing rests; a slower earlier answer never wins. */
function useAnswers(props: PartnerPickerProps, query: string, open: boolean): PickerPartner[] {
  const { search, debounceMs = 200 } = props;
  const [answer, setAnswer] = useState<PickerPartner[]>([]);
  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    const settle = (rows: PickerPartner[]): void => {
      if (current) setAnswer(rows);
    };
    const timer = setTimeout(
      () => void search(query.trim()).then(settle, () => settle([])),
      query ? debounceMs : 0,
    );
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [search, query, open, debounceMs]);
  return answer;
}

const companiesWord = (n: number): string =>
  `${n.toLocaleString('en-US')} ${n === 1 ? 'company' : 'companies'}`;

/** The picker's state: whether it is open, what is typed, which row the keyboard is on. */
function usePicker(props: PartnerPickerProps) {
  const { allLabel = 'Every partner', fixed = NO_PARTNER_ROW } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const found = useAnswers(props, query, open);
  const rows: Row[] = [
    { value: null, label: allLabel },
    ...fixed,
    ...found.map((p) => ({ value: p.code, label: p.code, note: companiesWord(p.companies) })),
  ];
  const close = (): void => {
    setOpen(false);
    setQuery('');
    setActive(0);
  };
  const pick = (index: number): void => {
    props.onChange(rows[index]?.value ?? null);
    close();
  };
  const type = (text: string): void => {
    setOpen(true);
    setActive(text ? 1 + fixed.length : 0);
    setQuery(text);
  };
  const move = (by: number): void =>
    open ? setActive((a) => (a + by + rows.length) % rows.length) : setOpen(true);
  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      pick(Math.min(active, rows.length - 1));
    }
  };
  return { open, setOpen, query, active, rows, fixed, allLabel, close, pick, type, onKey };
}

/**
 * The admin area's partner picker, built like the company picker: a box to type a code in, the server
 * answering with the codes that start that way (the partners with the most companies when nothing is
 * typed), twenty at most. The first row clears to every partner; "No partner" is always offered. Arrow keys
 * move, Enter picks, Escape closes. Never a `select` of every partner.
 */
export function PartnerPicker(props: PartnerPickerProps): ReactNode {
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const p = usePicker(props);
  const chosen = p.fixed.find((f) => f.value === props.value)?.label ?? props.value;
  return (
    <div
      className={`ad-picker ad-picker--partner${props.value ? ' ad-picker--on' : ''}`}
      ref={box}
      onBlur={(e) => {
        if (!box.current?.contains(e.relatedTarget)) p.close();
      }}
    >
      <input
        className="ad-picker__input"
        role="combobox"
        aria-label="Partner"
        aria-expanded={p.open}
        aria-controls={`${id}-list`}
        aria-activedescendant={p.open ? `${id}-${p.active}` : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        maxLength={4}
        placeholder={chosen ?? p.allLabel}
        value={p.open ? p.query : (chosen ?? '')}
        onFocus={() => p.setOpen(true)}
        onChange={(e) => p.type(e.currentTarget.value)}
        onKeyDown={p.onKey}
      />
      {p.open ? <Answers id={id} p={p} /> : null}
    </div>
  );
}

function Answers({ id, p }: { id: string; p: ReturnType<typeof usePicker> }): ReactNode {
  const codes = p.rows.length - 1 - p.fixed.length;
  return (
    <ul className="ad-picker__list" id={`${id}-list`} role="listbox" aria-label="Partners">
      {p.rows.map((row, i) => (
        <li
          key={row.value ?? ''}
          id={`${id}-${i}`}
          role="option"
          aria-selected={p.active === i}
          className={
            p.active === i ? 'ad-picker__option ad-picker__option--active' : 'ad-picker__option'
          }
          // The press, not the click: the input's blur would close the list before a click landed.
          onMouseDown={(e) => {
            e.preventDefault();
            p.pick(i);
          }}
        >
          <span>{row.label}</span>
          {row.note ? <span className="ad-picker__count">{row.note}</span> : null}
        </li>
      ))}
      <li className="ad-picker__note" role="presentation">
        {codes === 0 && p.query
          ? 'No partner code starts that way.'
          : 'Type the start of a partner code: twenty at most are listed.'}
      </li>
    </ul>
  );
}
